import { useState } from "react";
import {
  CalendarClock,
  Camera,
  Coffee,
  CreditCard,
  Flower2,
  GalleryHorizontalEnd,
  ImageIcon,
  Images,
  ShoppingBag,
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/ui/badge";
import { formatVND } from "@/lib/format";
import { customerRawLinkHref } from "./api";
import type { CustomerBooking, CustomerPortalOrder } from "./api";
import { usePublicBranches } from "./branches";
import { reopenPortalSelection, type PortalQR } from "./portal-api";
import { PortalPayDialog } from "./PortalPayDialog";
import { PortalShopDialog } from "./PortalShopDialog";
import { canRequestReschedule, RescheduleRequestForm } from "./RescheduleRequestForm";
import { ReviewEkipDialog } from "./ReviewEkipDialog";
import { TipDialog } from "./TipDialog";

const btnPrimary =
  "inline-flex min-h-11 items-center gap-2 rounded-xl bg-[var(--color-brand)] px-4 text-sm font-semibold text-white disabled:opacity-50";
const btnSecondary =
  "inline-flex min-h-11 items-center gap-2 rounded-xl border bg-surface-2 px-4 text-sm font-semibold text-primary disabled:opacity-50";

/**
 * Thẻ MỘT buổi chụp trên cổng khách (spec 22/08/2026 §8.5).
 *
 * Hàng tiền + Thanh toán (còn nợ) · Link chọn ảnh + Album của bạn + Chọn thêm ảnh (đơn album) · Mua sản
 * phẩm in (đã chụp xong) · Yêu cầu đổi lịch (đơn khách tự đặt online) · Chấm
 * điểm · Tip.
 *
 * KHÔNG bao giờ render link ảnh gốc chưa được chia sẻ — kho raw có cơ chế ghi
 * sổ riêng, không đưa lên trang công khai.
 *
 * `booking` = lịch khách tự đặt online đã sinh ra đơn này (nếu có). Truyền
 * vào để thẻ mọc thêm nút "Yêu cầu đổi lịch" — trước đây lịch nằm ở một danh
 * sách riêng nên khách đặt online thấy buổi chụp của mình hai lần.
 */
export function CustomerOrderCard({
  order,
  booking,
  navigate = (path) => window.location.assign(path),
}: {
  order: CustomerPortalOrder;
  // `| undefined` là bắt buộc: exactOptionalPropertyTypes bật, thiếu nó thì
  // truyền một giá trị có thể undefined vào đây là lỗi biên dịch.
  booking?: CustomerBooking | undefined;
  /** Chuyển trang sang màn chọn ảnh — mặc định đổi URL; bài kiểm truyền giả. */
  navigate?: (path: string) => void;
}) {
  const { branches: publicBranches } = usePublicBranches();
  const branch = publicBranches.find((item) => item.code === order.branch);
  const finalLinks = order.finalLinks ?? [];
  const selectionLink = (order.selectionLink ?? "").trim();
  const hasFinalShare = order.hasFinalShare === true;
  // Ảnh gốc: có ảnh là một chuyện, còn XEM được hay không là chuyện khác. Từ
  // quyết định 0106, cửa sổ đóng sau 48 giờ kể từ lượt xem đầu (trần cứng 30
  // ngày kể từ ngày chụp), nên nút phải bám vào cờ CÒN MỞ. Bám vào "có ảnh"
  // như trước là dựng một nút mà bấm vào chỉ nhận "không mở được album".
  const hasOriginalPhotos = order.hasOriginalPhotos === true && order.originalAlbumOpen === true;
  // Số link ảnh gốc khách được mở (quyết định 0061). Máy chủ trả SỐ, không trả
  // link — mỗi nút đi qua cửa ghi sổ rồi mới sang kho.
  const rawLinkCount = order.rawLinkCount ?? 0;
  // Đơn đã có album gốc trong Kho ảnh (gallery_albums kind='root'). Đây là
  // đường trả ảnh MỚI, đứng độc lập với link ngoài và vé phiên chọn ảnh.
  const hasGallery = order.hasGallery === true;
  // Số ảnh ĐÃ SỬA, đã qua cổng duyệt của leader (máy chủ trả 0 khi đơn còn ở
  // review/needFix). Đơn đi đường tải ảnh lên kho không ghi finalLinks và
  // không tự sinh link chia sẻ, nên đây là đường DUY NHẤT portal biết ảnh
  // hoàn thiện đã có.
  const finalPhotoCount = order.finalPhotoCount ?? 0;
  // Lấy THẲNG số của máy chủ, KHÔNG tự trừ "tổng − đã trả": máy chủ trừ cả
  // phiếu đang chờ kế toán xác nhận, nên hai số cố ý khác nhau. Tự trừ là
  // khách vừa chuyển khoản xong vẫn thấy nợ nguyên rồi trả lần nữa.
  const remaining = Math.max(0, order.remaining);
  // Chấm điểm mở sau khi giao ảnh (spec §5.1); tip mở mọi đơn chưa huỷ (§5.3).
  const canReview = order.status === "delivered";
  const canTip = order.status !== "cancelled";
  const canShop = order.canShop;
  const hasSession = order.hasPhotoSession;
  // Nút "Chọn thêm ảnh" (đường link) chỉ mời khi đơn CHƯA có phiên chọn ảnh —
  // và lý do là UX, KHÔNG phải vì máy chủ từ chối. Từ 18/09/2026 (QĐ 0168) máy
  // chủ mở cả hai đường cho mọi gói; nhưng đơn đã có phiên thì nút "Xem ảnh &
  // chọn thêm" ngay trên kia đã dẫn khách vào đúng tập ảnh họ đang tick, và
  // màn đó còn có sẵn hộp trả tiền. Dựng thêm một nút "Chọn thêm ảnh" trông y
  // hệt bên cạnh là bắt khách đoán hai nút khác nhau chỗ nào.
  const canReopenAlbum =
    !hasSession && (order.status === "completed" || order.status === "delivered");
  // Chỉ buổi khách TỰ ĐẶT ONLINE mới đổi lịch qua trang được; khách tới quầy
  // đổi lịch bằng cách gọi studio.
  const canReschedule = Boolean(booking) && canRequestReschedule(booking!.status);

  const [reviewOpen, setReviewOpen] = useState(false);
  const [tipOpen, setTipOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [payQR, setPayQR] = useState<PortalQR | null>(null);
  const [shopOpen, setShopOpen] = useState(false);
  const [busy, setBusy] = useState<"album" | null>(null);

  const reopenAlbum = async () => {
    setBusy("album");
    try {
      navigate((await reopenPortalSelection(order.id)).path);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Không mở lại được chọn ảnh");
    } finally {
      setBusy(null);
    }
  };

  return (
    <article className="rounded-3xl border bg-surface p-4 shadow-sm sm:p-5">
      <div className="flex flex-wrap items-center gap-2">
        <Badge
          variant={
            order.status === "delivered" || order.status === "completed" ? "green" : "outline"
          }
        >
          {orderStatusLabel(order.status)}
        </Badge>
        <span className="text-xs text-muted-foreground">#{order.code}</span>
      </div>
      {/* Tên gói đọc được; máy chủ không tra ra thì mới đành hiện mã. */}
      <h3 className="mt-3 text-lg font-semibold">{order.productName?.trim() || order.product}</h3>
      <div className="mt-2 grid gap-1 text-sm text-muted-foreground">
        {order.shootDate && (
          <span className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-primary" />
            {formatOrderDate(order.shootDate)}
            {order.startTime
              ? ` · ${order.startTime}${order.endTime ? `-${order.endTime}` : ""}`
              : ""}
          </span>
        )}
        <span>{order.branchName || branch?.shortLabel || order.branch}</span>
      </div>

      <MoneyBand
        payable={order.payableTotal}
        paid={order.paidTotal}
        refunded={order.refundedTotal ?? 0}
        remaining={remaining}
        onPay={() => setPayOpen(true)}
      />

      <PhotoLinks
        orderId={order.id}
        hasSession={hasSession}
        selectionLink={selectionLink}
        finalLinks={finalLinks}
        hasGallery={hasGallery}
        hasFinalShare={hasFinalShare}
        hasOriginalPhotos={hasOriginalPhotos}
        rawLinkCount={rawLinkCount}
        finalPhotoCount={finalPhotoCount}
        canReopenAlbum={canReopenAlbum}
        busy={busy}
        onReopenAlbum={() => void reopenAlbum()}
      />

      {(canShop || canReview || canTip || canReschedule) && (
        <div className="mt-4 flex flex-wrap items-start gap-2 border-t pt-3">
          {canReschedule && <RescheduleRequestForm bookingId={booking!.id} />}
          {canShop && (
            <button type="button" className={btnPrimary} onClick={() => setShopOpen(true)}>
              <ShoppingBag className="h-4 w-4" /> Mua sản phẩm in
            </button>
          )}
          {canReview && (
            <button type="button" className={btnSecondary} onClick={() => setReviewOpen(true)}>
              <Flower2 className="h-4 w-4" /> Chấm điểm ê-kíp
            </button>
          )}
          {canTip && (
            <button type="button" className={btnSecondary} onClick={() => setTipOpen(true)}>
              <Coffee className="h-4 w-4" /> Mời ê-kíp ly cà phê
            </button>
          )}
        </div>
      )}

      {payOpen && (
        <PortalPayDialog
          orderId={order.id}
          // Có phiếu đơn in thì đưa THẲNG phiếu đó xuống; truyền kèm số công nợ
          // nữa là màn trả tiền có hai nguồn số, dễ thu nhầm khoản.
          {...(payQR
            ? { initialQR: payQR, title: "Thanh toán sản phẩm in" }
            : { amount: remaining })}
          open={payOpen}
          // Đóng màn là XOÁ phiếu đơn in — đây là chỗ DUY NHẤT xoá. Sót lại thì
          // lần bấm "Thanh toán" công nợ sau đó mở đúng mã của đơn in, khách
          // trả tiền in lần thứ hai mà công nợ đơn chụp không suy chuyển.
          onOpenChange={(o) => {
            setPayOpen(o);
            if (!o) setPayQR(null);
          }}
        />
      )}
      {canShop && (
        <PortalShopDialog
          orderId={order.id}
          open={shopOpen}
          onOpenChange={setShopOpen}
          onCreated={(res) => {
            setShopOpen(false);
            // `qr` chỉ vắng ở lượt nhân viên đặt hộ (QĐ 0290) — màn khách này không
            // bao giờ chạy ở chế độ đó; vắng thì hộp trả tiền tự ra mã mới.
            setPayQR(res.qr ?? null);
            setPayOpen(true);
          }}
        />
      )}
      {canReview && (
        <ReviewEkipDialog
          orderId={order.id}
          open={reviewOpen}
          onOpenChange={setReviewOpen}
          onInviteTip={canTip ? () => setTipOpen(true) : undefined}
        />
      )}
      {canTip && <TipDialog orderId={order.id} open={tipOpen} onOpenChange={setTipOpen} />}
    </article>
  );
}

function orderStatusLabel(status: string) {
  switch (status) {
    case "booked":
      return "Đã đặt lịch";
    case "shooting":
      return "Đang chụp";
    case "editing":
      return "Đang hậu kỳ";
    case "completed":
      return "Hoàn thiện";
    case "delivered":
      return "Đã giao ảnh";
    case "cancelled":
      return "Đã huỷ";
    default:
      return status;
  }
}

function formatOrderDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("vi-VN", { dateStyle: "medium" }).format(date);
}

/** Hàng tiền + lối trả nốt. Số `remaining` do máy chủ tính, màn hình không tự trừ. */
function MoneyBand({
  payable,
  paid,
  refunded,
  remaining,
  onPay,
}: {
  payable: number;
  paid: number;
  refunded: number;
  remaining: number;
  onPay: () => void;
}) {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-2xl bg-surface-2 px-4 py-3 text-sm">
      <span>
        Tổng: <b>{formatVND(payable)}</b>
      </span>
      <span>
        Đã trả: <b>{formatVND(paid)}</b>
      </span>
      {refunded > 0 ? (
        <span>
          Đã hoàn: <b>{formatVND(refunded)}</b>
        </span>
      ) : null}
      {remaining > 0 ? (
        <>
          <span className="font-medium text-danger-text">Còn lại: {formatVND(remaining)}</span>
          <button type="button" className={`${btnPrimary} ml-auto`} onClick={onPay}>
            <CreditCard className="h-4 w-4" /> Thanh toán {formatVND(remaining)}
          </button>
        </>
      ) : (
        <span className="ml-auto font-medium text-success-text">Đã thanh toán đủ</span>
      )}
    </div>
  );
}

/**
 * Khối ảnh của khách.
 *
 * Đơn ảnh thẻ chọn theo đơn trên Album của Cổng (QĐ 0398 — nút "Xem ảnh &
 * chọn thêm" bằng vé phiên đã gỡ; trang này vốn luôn bị đẩy sang Cổng); đơn
 * album đi đường link. Từ
 * 18/09/2026 (QĐ 0168) máy chủ KHÔNG còn từ chối đường nào theo loại đơn: khách
 * chọn dở ở quầy rồi về nhà chọn nốt là cảnh chạy đúng. Nên `hasSession` ở đây
 * chỉ còn là cái chuyển hướng CHỌN ẢNH — nó phải đứng NGOÀI mấy nút GIAO ẢNH
 * (album của bạn / album hoàn thiện): hai chuyện khác nhau, gộp vào là đơn nào
 * từng đưa iPad cho khách thì khách mất luôn đường vào album đã chỉnh.
 */
function PhotoLinks({
  orderId,
  hasSession,
  selectionLink,
  finalLinks,
  hasGallery,
  hasFinalShare,
  hasOriginalPhotos,
  rawLinkCount,
  finalPhotoCount,
  canReopenAlbum,
  busy,
  onReopenAlbum,
}: {
  orderId: string;
  hasSession: boolean;
  selectionLink: string;
  finalLinks: string[];
  /** Đơn đã có album trong Kho ảnh — xem thẳng trên cổng khách. */
  hasGallery: boolean;
  /** CSKH đã bấm chia sẻ album hoàn thiện — xem ngay trên cổng, không qua link ngoài. */
  hasFinalShare: boolean;
  /** Ảnh gốc của đơn CÒN xem được không — đã gộp cả cửa sổ 48 giờ (0106). */
  hasOriginalPhotos: boolean;
  /** Số link ảnh gốc dán trên đơn/pipeline mà khách được mở (quyết định 0061). */
  rawLinkCount: number;
  /** Số ảnh đã duyệt trong album của đơn; máy chủ đã áp cổng duyệt của leader. */
  finalPhotoCount: number;
  canReopenAlbum: boolean;
  busy: "album" | null;
  onReopenAlbum: () => void;
}) {
  // Ảnh hoàn thiện xong nhưng chưa ai bấm gửi link — khách đang chờ STUDIO,
  // không phải chờ thợ. Ghi chú này phải đứng ĐỘC LẬP với các nút phía trên:
  // đơn album luôn có ảnh gốc nên nút "Xem ảnh gốc" đã bật sẵn, và khách nhìn
  // thấy ảnh gốc lại tưởng đó là tất cả những gì mình được nhận.
  const awaitingShareLink = finalPhotoCount > 0 && finalLinks.length === 0 && !hasFinalShare;
  return (
    <div className="mt-4 border-t pt-3">
      <div className="text-sm font-semibold">Ảnh của bạn</div>
      <div className="mt-2 flex flex-wrap gap-2">
        {hasGallery && (
          <a href={`/booking/me/orders/${orderId}/gallery`} className={btnPrimary}>
            <GalleryHorizontalEnd className="h-4 w-4" /> Xem album
          </a>
        )}
        {!hasSession && selectionLink !== "" && (
          <a
            href={selectionLink}
            target="_blank"
            rel="noopener noreferrer"
            className={btnSecondary}
          >
            <Camera className="h-4 w-4" /> Link chọn ảnh
          </a>
        )}
        {/* Nút GIAO ẢNH — cố ý KHÔNG gác theo `hasSession`. Đây là album ảnh đã
            chỉnh của khách, không liên quan gì tới việc họ chọn ảnh ở màn nào. */}
        {finalLinks.map((link, index) => (
          <a
            key={link}
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className={btnPrimary}
          >
            <ImageIcon className="h-4 w-4" />
            {finalLinks.length > 1 ? `Album của bạn ${index + 1}` : "Album của bạn"}
          </a>
        ))}
        {hasFinalShare && finalLinks.length === 0 && (
          <a href={`/booking/me/orders/${orderId}/album`} className={btnPrimary}>
            <ImageIcon className="h-4 w-4" /> Xem album hoàn thiện
          </a>
        )}
        {hasOriginalPhotos && (
          <a href={`/booking/me/orders/${orderId}/original-album`} className={btnSecondary}>
            <Images className="h-4 w-4" /> Xem ảnh gốc
          </a>
        )}
        {/* Ảnh gốc dạng LINK NGOÀI (Drive/Dropbox studio dán vào đơn). href trỏ
            về máy chủ để ghi sổ rồi mới 302 — không render link kho ra HTML.
            Đứng riêng với nút trên: nút kia là ảnh gốc đã tải lên kho của mình,
            còn đây là kho ngoài; một đơn có thể có cả hai. */}
        {Array.from({ length: rawLinkCount }, (_, i) => (
          <a
            key={i}
            href={customerRawLinkHref(orderId, i)}
            target="_blank"
            rel="noopener noreferrer"
            className={btnSecondary}
          >
            <Images className="h-4 w-4" />
            {rawLinkCount > 1 ? `Ảnh gốc ${i + 1}` : "Ảnh gốc"}
          </a>
        ))}
        {canReopenAlbum && (
          <button
            type="button"
            className={btnSecondary}
            disabled={busy === "album"}
            onClick={onReopenAlbum}
          >
            <ImageIcon className="h-4 w-4" />
            {busy === "album" ? "Đang mở…" : "Chọn thêm ảnh"}
          </button>
        )}
        {!hasSession &&
          !hasGallery &&
          finalLinks.length === 0 &&
          selectionLink === "" &&
          !hasFinalShare &&
          !hasOriginalPhotos &&
          rawLinkCount === 0 &&
          !canReopenAlbum && (
            <p className="text-sm text-muted-foreground">
              Chưa có link ảnh — album sẽ được CSKH cập nhật sau buổi chụp. Bạn cần hỗ trợ nhanh vui
              lòng liên hệ studio.
            </p>
          )}
      </div>
      {awaitingShareLink && (
        <p className="mt-2 text-sm text-muted-foreground">
          Ảnh hoàn thiện của bạn đã xong ({finalPhotoCount} ảnh) — studio sẽ gửi link xem ảnh cho
          bạn. Bạn cần gấp vui lòng liên hệ studio.
        </p>
      )}
    </div>
  );
}
